import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Activity, AlarmClock, BadgeIndianRupee, Bell, BookOpen, Briefcase, CalendarDays, CheckSquare, ClipboardList, Cog, Eye, FileBarChart,
  Gauge, Layers, LayoutDashboard, ListOrdered, LogOut, Radar, Receipt, Search, ShieldAlert, ShieldCheck, SlidersHorizontal, StickyNote, Users, UserCog, Wrench } from "lucide-react";
import { useBo } from "./AdminAuth";
import { boApi } from "./api";
import { SimBadge } from "../components/SimBadge";

export const NAV = [
  ["Dashboard", "", LayoutDashboard, "dash.view"], ["Clients", "clients", Users, "data.view"], ["Funds & ledger", "funds", BadgeIndianRupee, "data.view"],
  ["Orders", "orders", ListOrdered, "data.view"], ["Positions", "positions", Layers, "data.view"], ["Holdings", "holdings", Briefcase, "data.view"],
  ["Trades", "trades", Receipt, "data.view"], ["GTT & alerts", "gtt", AlarmClock, "data.view"], ["Risk & RMS", "risk", ShieldAlert, "data.view"],
  ["Surveillance", "surveillance", Radar, "data.view"], ["Live monitor", "monitor", Activity, "monitor.view"], ["Instruments", "instruments", BookOpen, "ops.view"],
  ["Market calendar", "calendar", CalendarDays, "ops.view"], ["Corporate actions", "corporate-actions", Wrench, "ops.view"],
  ["Brokerage & charges", "brokerage", Gauge, "data.view"], ["Trading settings", "trading-settings", SlidersHorizontal, "data.view"],
  ["Notifications", "notifications", Bell, "data.view"], ["Reports", "reports", FileBarChart, "reports.view"], ["Support notes", "notes", StickyNote, "data.view"],
  ["Approvals", "approvals", CheckSquare, "dash.view"], ["Audit log", "audit", ClipboardList, "audit.view"], ["Admin users", "admins", UserCog, "admins.manage"],
  ["System health", "health", Eye, "ops.view"], ["Settings", "settings", Cog, "settings.view"],
];

function GlobalSearch() {
  const { can } = useBo();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const t = useRef(null);
  useEffect(() => {
    clearTimeout(t.current);
    if (q.trim().length < 2 || !can("data.view")) { setRes(null); return undefined; }
    t.current = setTimeout(() => boApi.get("/search", { params: { q } }).then((r) => setRes(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t.current);
  }, [q, can]);
  const go = (path) => { setQ(""); setRes(null); nav(path); };
  return (
    <div className="relative w-full max-w-md">
      <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
      <input data-testid="global-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client name, email, phone, client ID, order ID"
        className="h-9 w-full rounded-md border border-input bg-background pl-8 pr-3 text-sm outline-none focus:border-primary" />
      {res && (
        <div data-testid="global-search-results" className="absolute left-0 right-0 top-10 z-50 max-h-96 overflow-auto rounded-md border border-border bg-popover p-1 shadow-xl">
          {res.clients.map((c) => (
            <button key={c.id} data-testid={`search-client-${c.id}`} onClick={() => go(`/admin/clients/${c.id}`)} className="flex w-full justify-between rounded px-2 py-1.5 text-left text-xs hover:bg-accent">
              <span>{c.name} · {c.email}</span><span className="text-muted-foreground">{c.login || c.id.slice(0, 8).toUpperCase()}</span>
            </button>
          ))}
          {res.orders.map((o) => (
            <button key={o.id} data-testid={`search-order-${o.id}`} onClick={() => go(`/admin/orders/${o.id}`)} className="flex w-full justify-between rounded px-2 py-1.5 text-left text-xs hover:bg-accent">
              <span>Order {o.id.slice(0, 10)} · {o.side} {o.quantity} {o.symbol}</span><span className="text-muted-foreground">{o.status}</span>
            </button>
          ))}
          {res.clients.length + res.orders.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">No matches</div>}
        </div>
      )}
    </div>
  );
}

function InboxBell() {
  const [n, setN] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    const load = () => boApi.get("/inbox").then((r) => setN(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);
  const total = n ? n.approvals + n.account_failed + n.account_stuck + n.surveillance + n.follow_ups : 0;
  return (
    <button data-testid="inbox-bell" title={n ? `${n.approvals} approvals · ${n.account_failed + n.account_stuck} account issues · ${n.surveillance} surveillance · ${n.follow_ups} follow-ups` : ""}
      onClick={() => nav(n?.account_failed || n?.account_stuck ? "/admin/clients?tab=queue" : "/admin/approvals")} className="relative rounded-md p-2 hover:bg-accent">
      <Bell className="h-4 w-4" />
      {total > 0 && <span data-testid="inbox-count" className="absolute -right-0.5 -top-0.5 rounded-full bg-destructive px-1 text-[9px] font-bold text-white">{total}</span>}
    </button>
  );
}

export default function Layout() {
  const { admin, can, logout } = useBo();
  return (
    <div className="dark flex h-screen bg-background text-foreground">
      <aside data-testid="bo-sidebar" className="hidden w-56 shrink-0 flex-col border-r border-border bg-card md:flex">
        <div className="flex items-center gap-2 border-b border-border px-4 py-3.5">
          <ShieldCheck className="h-5 w-5 text-primary" />
          <div className="leading-tight"><div className="font-display text-sm font-bold">Greenwick</div><div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Back-office</div></div>
        </div>
        <nav className="flex-1 overflow-y-auto p-2">
          {NAV.filter(([, , , p]) => can(p)).map(([label, path, Icon]) => (
            <NavLink key={path} end={path === ""} to={`/admin/${path}`} data-testid={`nav-${path || "dashboard"}`}
              className={({ isActive }) => `mb-0.5 flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[12.5px] transition-colors duration-150 ${isActive ? "bg-primary/15 font-semibold text-primary" : "text-muted-foreground hover:bg-accent hover:text-foreground"}`}>
              <Icon className="h-3.5 w-3.5" />{label}
            </NavLink>
          ))}
        </nav>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center gap-3 border-b border-border bg-card/80 px-4 py-2 backdrop-blur-xl">
          <select data-testid="mobile-nav" className="h-9 rounded-md border border-input bg-background px-2 text-xs md:hidden" onChange={(e) => (window.location.href = `/admin/${e.target.value}`)} value="">
            <option value="">Menu</option>
            {NAV.filter(([, , , p]) => can(p)).map(([l, p]) => <option key={p} value={p}>{l}</option>)}
          </select>
          {can("data.view") ? <GlobalSearch /> : <div className="flex-1" />}
          <span data-testid="env-badge" className="hidden rounded border border-red-500/50 bg-red-500/10 px-2 py-0.5 text-[10px] font-bold tracking-[0.16em] text-red-400 sm:inline">PRODUCTION</span>
          <div className="ml-auto flex items-center gap-2">
            <SimBadge testid="bo-sim-badge" className="hidden lg:inline-flex" />
            <InboxBell />
            <div className="hidden text-right leading-tight sm:block">
              <div data-testid="admin-name" className="text-xs font-semibold">{admin.name || admin.email}</div>
              <div data-testid="admin-role" className="text-[10px] uppercase tracking-wider text-primary">{admin.role.replace("_", " ")}</div>
            </div>
            <button data-testid="bo-logout-btn" onClick={() => logout()} className="rounded-md p-2 hover:bg-accent" title="Sign out"><LogOut className="h-4 w-4" /></button>
          </div>
        </header>
        <div className="flex justify-center border-b border-amber-500/30 bg-amber-500/10 py-1 lg:hidden"><SimBadge testid="bo-sim-badge-mobile" className="border-0 bg-transparent" /></div>
        <main className="flex-1 overflow-y-auto p-4 md:p-6"><Outlet /></main>
      </div>
    </div>
  );
}
